import { type FC, useState } from "react";
import { useDoc, usePouch, useView } from "use-pouchdb";
import { Link } from "react-router";

import { assertIsItemInfo, isItemInfo, itemLinkId, type ItemInfo } from "../common/schema";
import { isPouchError } from "./utils";
import { useScanner } from "./Scanner";
import { IconImage, IconNameDisplay } from "./images";
import { IconPicker, ItemPicker } from "./pickers";
import { ItemItem } from "./ItemItem";

export const ItemChildren:FC<{id: string}> = ({id}) => {
  const { rows } = useView("built/by_in", { key: id, include_docs: true });
  const db = usePouch();
  const [showMoveIntoPicker, setShowMoveIntoPicker] = useState(false);
  return <div className="children">
    {showMoveIntoPicker
      ? <ItemPicker
          label="Move item into here…"
          excludeId={id}
          onSelect={childId => {
            db.get(childId).then(childDoc => {
              assertIsItemInfo(childDoc);
              return db.put({...childDoc, in: id, wherein: null});
            }).catch(console.error);
            // setShowMoveIntoPicker(false);
          }}
          onCancel={() => setShowMoveIntoPicker(false)}
        />
      : <button type="button" onClick={() => setShowMoveIntoPicker(true)} style={{marginTop: "4px"}}>Move item in here…</button>
    }
    {rows.map((row) => {
      assertIsItemInfo(row.doc);
      return <div key={row.doc._id} style={{display: "flex", flexDirection: "row"}}>
        <button type="button" onClick={() => {
          const updated = {...row.doc, in: null, wherein: null};
          db.put(updated).catch(console.error);
        }}>↑</button>
        <ItemItem doc={row.doc} />
      </div>;
    })}
  </div>;
};

// ── Tags ─────────────────────────────────────────────────────────────────────

export const TagEditor: FC<{tags: string[]; onAdd: (t: string) => void; onRemove: (t: string) => void}> = ({tags, onAdd, onRemove}) => {
  const [input, setInput] = useState("");
  const submit = () => {
    const tag = input.trim();
    if (tag && !tags.includes(tag)) onAdd(tag);
    setInput("");
  };
  return <div style={{display: "flex", flexWrap: "wrap", gap: "4px", alignItems: "center"}}>
    {tags.map(tag => (
      <span key={tag} style={{display: "inline-flex", alignItems: "center", gap: "2px", padding: "2px 6px", background: "#e0e0e0", borderRadius: "12px", fontSize: "small"}}>
        {tag}
        <button type="button" onClick={() => onRemove(tag)} style={{border: "none", background: "none", cursor: "pointer", padding: "0 2px", fontSize: "12px"}}>×</button>
      </span>
    ))}
    <input
      type="text"
      value={input}
      onChange={e => setInput(e.target.value)}
      onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
      placeholder="add tag…"
      style={{width: "100px", fontSize: "small"}}
    />
    <button type="button" onClick={submit} style={{fontSize: "small"}}>+</button>
  </div>;
};

export function useAttrData(): { names: string[]; valuesByName: Record<string, string[]> } {
  const { rows } = useView("built/not_deleted", { include_docs: true });
  const names = new Set<string>();
  const valuesByName: Record<string, Set<string>> = {};
  for (const row of rows) {
    if (row.doc == null) continue;
    if (!isItemInfo(row.doc)) continue;
    for (const [k, v] of Object.entries(row.doc.attrs ?? {})) {
      names.add(k);
      if (!(k in valuesByName)) valuesByName[k] = new Set();
      valuesByName[k]!.add(v);
    }
  }
  return {
    names: [...names].sort(),
    valuesByName: Object.fromEntries(Object.entries(valuesByName).map(([k, s]) => [k, [...s].sort()])),
  };
}

// ── Scancodes ─────────────────────────────────────────────────────────────────

export const ScancodeEditor: FC<{
  scancodes: Array<{code: string; type: string}>;
  onAdd: (entry: {code: string; type: string}) => void;
  onRemove: (index: number) => void;
}> = ({scancodes, onAdd, onRemove}) => {
  const [input, setInput] = useState("");
  const { startScanner } = useScanner({
    onScanResult: (result) => {
      if (result == null) return;
      const typeName = result.format;
      onAdd({ code: result.text, type: typeName });
    },
    defaults: { allFormats: true },
  });
  const addManual = () => {
    const code = input.trim();
    if (code) { onAdd({ code, type: "Unknown" }); setInput(""); }
  };
  return <div>
    <table style={{width: "100%", fontSize: "small", borderCollapse: "collapse"}}>
      <tbody>
        {scancodes.map((s, i) => (
          <tr key={i}>
            <td style={{fontFamily: "monospace", paddingRight: "8px"}}>{s.code}</td>
            <td style={{color: "#666"}}>{s.type}</td>
            <td><button type="button" onClick={() => onRemove(i)} style={{border: "none", background: "none", cursor: "pointer"}}>×</button></td>
          </tr>
        ))}
      </tbody>
    </table>
    <div style={{display: "flex", gap: "4px", alignItems: "center", marginTop: "4px"}}>
      <input type="text" value={input} onChange={e => setInput(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addManual(); } }}
        placeholder="barcode value" style={{flexGrow: 1, fontSize: "small"}} />
      <button type="button" onClick={addManual} style={{fontSize: "small"}}>+</button>
      <button type="button" onClick={startScanner} title="Scan barcode" style={{fontSize: "small"}}>📷</button>
    </div>
  </div>;
};

// ── Icon assignment ────────────────────────────────────────────────────────────

const iconThumbStyle: React.CSSProperties = {width: "50px", height: "50px", flexShrink: 0, backgroundColor: "#ddd"};

export const ItemIconSection: FC<{doc: ItemInfo}> = ({doc}) => {
  const db = usePouch();
  const [showPicker, setShowPicker] = useState(false);

  const setIcon = (iconId: string) => {
    db.put({...doc, icon_id: iconId}).catch(console.error);
    setShowPicker(false);
  };

  const clearIcon = () => {
    db.put({...doc, icon_id: null}).catch(console.error);
  };

  return <div style={{marginTop: "8px"}}>
    <strong>Icon</strong>
    {doc.icon_id != null && (
      <div style={{display: "flex", alignItems: "center", gap: "8px", marginTop: "4px"}}>
        <IconImage iconId={doc.icon_id} style={iconThumbStyle} />
        <IconNameDisplay iconId={doc.icon_id} />
        <button type="button" onClick={clearIcon}>Clear</button>
      </div>
    )}
    {showPicker
      ? <IconPicker onSelect={setIcon} onCancel={() => setShowPicker(false)} />
      : <button type="button" onClick={() => setShowPicker(true)} style={{marginTop: "4px"}}>
          {doc.icon_id != null ? "Change icon…" : "Set from icon library…"}
        </button>
    }
  </div>;
};

// ── Friends (many-many item links) ───────────────────────────────────────────

const FriendItem: FC<{friendId: string}> = ({friendId}) => {
  const { doc } = useDoc<Partial<ItemInfo>>(friendId);
  let name = friendId;
  if (doc != null) {
    assertIsItemInfo(doc);
    name = doc.name || friendId;
  }
  return <Link to={`/items/${friendId}`}><div>{name}</div></Link>;
};

export const ItemFriends: FC<{itemId: string}> = ({itemId}) => {
  const db = usePouch();
  const { rows } = useView("built/by_linked_item", { key: itemId });
  const [showPicker, setShowPicker] = useState(false);

  const addFriend = async (otherId: string) => {
    const linkId = itemLinkId(itemId, otherId);
    let existing: { _rev: string, } | null = null;
    try {
      existing = await db.get(linkId);
    } catch (e: unknown) {
      if (!isPouchError(e, "not_found"))
        throw e;
    }
    const newDoc:{_id: string, deleted_at: null, _rev?: string} = {
      _id: linkId,
      deleted_at: null,
    };
    if (existing != null) {
      newDoc._rev = existing._rev;
    }
    await db.put(newDoc, {force: true});
    setShowPicker(false);
  };

  const removeFriend = async (otherId: string) => {
    const linkId = itemLinkId(itemId, otherId);
    const existing = await db.get(linkId);
    await db.put({ ...existing, deleted_at: new Date().toISOString() });
  };

  return <div style={{marginTop: "8px"}}>
    <strong>Friends</strong>
    {rows.map(row => {
      const friendId: string = row.value;
      return <div key={friendId} style={{display: "flex", flexDirection: "row", alignItems: "center"}}>
        <button type="button" onClick={() => removeFriend(friendId).catch(console.error)} style={{marginRight: "4px"}}>×</button>
        <FriendItem friendId={friendId} />
      </div>;
    })}
    {showPicker
      ? <ItemPicker
          label="Link friend…"
          excludeId={itemId}
          onSelect={id => addFriend(id).catch(console.error)}
          onCancel={() => setShowPicker(false)}
        />
      : <button type="button" onClick={() => setShowPicker(true)} style={{marginTop: "4px"}}>Link friend…</button>
    }
  </div>;
};
